/**
 * TenantModeContext — public entry. Picks the mock (Phase 1) or the
 * real-backend (Phase 3) tenant data provider based on
 * `REACT_APP_USE_MOCK_DATA`. Both providers write to the same React
 * context so every consumer (`useTenantData`) is provider-agnostic.
 *
 * Architecture rules:
 *  1. ONE application, ONE codebase, ONE UI, ONE workflow engine.
 *  2. The ONLY difference between Demo Mode and Customer Mode is the
 *     underlying data source.
 *  3. The Demo Mode uses a *registry* of pre-loaded tenants.
 *  4. Customer Mode loads an empty bundle, which drives the guided
 *     onboarding / empty-state experience throughout the app.
 */
import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import {
  DEMO_TENANT_REGISTRY,
  DEFAULT_DEMO_TENANT_ID,
  getDemoTenantBundle,
  listDemoTenants,
} from "@/lib/demoData";
import { buildOpportunitySubmission } from "@/lib/opportunitySubmission";
import { applyDecision } from "@/lib/deploymentWorkflow";
import { USE_MOCK_DATA } from "@/lib/api";
import { ApiTenantProvider } from "@/contexts/ApiTenantContext";
import {
  readMode,
  writeMode,
  readDemoTenantId,
  writeDemoTenantId,
} from "@/lib/uiPreferences";

export const TenantModeContext = createContext(null);

export function useTenantData() {
  const ctx = useContext(TenantModeContext);
  if (!ctx) throw new Error("useTenantData must be used inside TenantModeProvider");
  return ctx;
}

const TENANT_CONTEXT_INTERNAL = TenantModeContext;

const CUSTOMER_TENANT = {
  id: "customer-tenant",
  name: "Your Organisation",
  industry: null,
  region: "AU · Sydney",
  residency: "ap-southeast-2",
  tier: "ENTERPRISE",
  domain: null,
  contractStart: new Date().toISOString().slice(0, 10),
  cmkId: null,
};

const EMPTY_BUNDLE = {
  tenant: CUSTOMER_TENANT,
  vendors: [],
  vendorDetails: {},
  vendorCatalogue: [],
  agents: [],
  opportunities: [],
  policies: [],
  incidents: [],
  hilQueue: [],
  driftSignals: [],
  requests: [],
  frameworks: [],
  auditEntries: [],
  reports: [],
  reportPacks: [],
  scheduledReports: [],
  costTrend: [],
  costOpportunities: [],
  costByDepartment: [],
  benchmark: null,
  users: [],
  departmentRollups: [],
  vendorRollups: [],
  vendorBurnRollup: [],
  topBurners: [],
  trend12m: [],
  kpis: {
    governedAgents: 0,
    totalAgents: 0,
    monthlySpendAud: 0,
    monthlyValueAud: 0,
    roiRatio: 0,
    openHighIncidents: 0,
    violations: 0,
    ungoverned: 0,
    pendingApprovals: 0,
    avgComplianceCoverage: 0,
  },
  isEmpty: true,
};

// Static at module load — the registry never mutates at runtime.
const ALLOWED_TENANT_IDS = new Set(Object.keys(DEMO_TENANT_REGISTRY));
const AVAILABLE_DEMO_TENANTS = listDemoTenants();

const initialMode = () => readMode();
const initialDemoTenantId = () => readDemoTenantId(ALLOWED_TENANT_IDS, DEFAULT_DEMO_TENANT_ID);

const EMPTY_OVERLAY = {
  opportunities: [],
  requests: [],
  requestOverrides: {},
  auditEntries: [],
};

/**
 * Merge the session overlay (user-created opportunities / requests /
 * audit entries / request stage updates) over the canonical base bundle.
 * Returns the base reference unchanged when overlay is empty so React
 * skips downstream re-renders.
 */
function mergeOverlay(mode, demoTenantId, overlay) {
  const base = mode === "customer" ? EMPTY_BUNDLE : getDemoTenantBundle(demoTenantId);
  const overrideCount = Object.keys(overlay.requestOverrides).length;
  const isEmpty =
    !overlay.opportunities.length &&
    !overlay.requests.length &&
    !overlay.auditEntries.length &&
    overrideCount === 0;
  if (isEmpty) return base;

  const applyOverride = (r) => overlay.requestOverrides[r.id] || r;
  return {
    ...base,
    opportunities: [...overlay.opportunities, ...base.opportunities],
    requests: [...overlay.requests.map(applyOverride), ...base.requests.map(applyOverride)],
    auditEntries: [...overlay.auditEntries, ...base.auditEntries],
  };
}

export function TenantModeProvider({ children }) {
  if (!USE_MOCK_DATA) return <ApiTenantProvider>{children}</ApiTenantProvider>;
  return <MockTenantProvider>{children}</MockTenantProvider>;
}

function MockTenantProvider({ children }) {
  const [mode, setModeState] = useState(initialMode);
  const [demoTenantId, setDemoTenantIdState] = useState(initialDemoTenantId);
  // In-memory overlay — opportunities + requests + audit entries created by
  // the user in this session. Cleared when mode flips so each tenant starts
  // from its canonical baseline.
  const [overlay, setOverlay] = useState(EMPTY_OVERLAY);

  const setMode = useCallback((next) => {
    setModeState(next);
    setOverlay(EMPTY_OVERLAY);
    writeMode(next);
  }, []);

  const setDemoTenantId = useCallback((id) => {
    if (!ALLOWED_TENANT_IDS.has(id)) return;
    setDemoTenantIdState(id);
    setOverlay(EMPTY_OVERLAY);
    writeDemoTenantId(id);
  }, []);

  /**
   * Submit a new AI Opportunity (S08). Delegates the artefact construction
   * to `buildOpportunitySubmission` and only handles state mutation here.
   * Returns a Promise so the signature matches `ApiTenantProvider`, which
   * is genuinely async (network call).
   */
  const submitOpportunityRequest = useCallback(async (payload) => {
    const { opportunity, request, audit } = buildOpportunitySubmission(payload);
    setOverlay((prev) => ({
      ...prev,
      opportunities: [opportunity, ...prev.opportunities],
      requests: [request, ...prev.requests],
      auditEntries: [audit, ...prev.auditEntries],
    }));
    return { opportunity, request, audit };
  }, []);

  /**
   * Advance a deployment request through the workflow (S09–S11).
   *
   *   decision: "approve" | "reject" | "changes" | "deploy"
   *   actor:    the current persona (used for the audit trail)
   *
   * Pure logic lives in lib/deploymentWorkflow.applyDecision. This callback
   * just records the override + audit entry in the session overlay.
   * Returns a Promise so the signature matches the async ApiTenantProvider.
   */
  const advanceDeploymentStage = useCallback(async (currentRequest, decision, actor) => {
    const { request: updated, audit } = applyDecision(currentRequest, decision, actor);
    setOverlay((prev) => ({
      ...prev,
      requestOverrides: { ...prev.requestOverrides, [updated.id]: updated },
      auditEntries: audit ? [audit, ...prev.auditEntries] : prev.auditEntries,
    }));
    return { request: updated, audit };
  }, []);

  const recordAuditChainVerify = useCallback((auditEntry) => {
    if (!auditEntry) return;
    setOverlay((prev) => ({
      ...prev,
      auditEntries: [auditEntry, ...prev.auditEntries],
    }));
  }, []);

  const bundle = useMemo(() => mergeOverlay(mode, demoTenantId, overlay), [mode, demoTenantId, overlay]);

  const value = useMemo(
    () => ({
      mode,
      setMode,
      demoTenantId,
      setDemoTenantId,
      availableDemoTenants: AVAILABLE_DEMO_TENANTS,
      isDemo: mode === "demo",
      isCustomer: mode === "customer",
      submitOpportunityRequest,
      advanceDeploymentStage,
      recordAuditChainVerify,
      // Shape parity with ApiTenantProvider (Phase 3b consumers).
      pendingEntities: {
        opportunities: overlay.opportunities,
        requests: overlay.requests,
        auditEntries: overlay.auditEntries,
        requestOverrides: overlay.requestOverrides,
      },
      ...bundle,
    }),
    [mode, setMode, demoTenantId, setDemoTenantId, submitOpportunityRequest, advanceDeploymentStage, recordAuditChainVerify, bundle, overlay],
  );

  return <TENANT_CONTEXT_INTERNAL.Provider value={value}>{children}</TENANT_CONTEXT_INTERNAL.Provider>;
}
